import { STAFF_USER_ROLE } from '../../environment';
import { FeatureFlags } from '../../state/feature-flags/feature-flags.slice';
import { Token } from '../../user/user.slice';
import { camelCasify } from '../../utils/text-formatting.utils';

export type RiPortPrivileges = {
  land: boolean;
  coastal: boolean;
  cruise: boolean;
};

export type Privileges = {
  canAccessIncidents: boolean;
  canAccessPorts: {
    wpi: boolean;
    riskIntelligence: false | RiPortPrivileges;
  };
  canAccessRiCountries: boolean;
  canAccessTravelAdvice: boolean;
  canAccessRoutes: boolean;
  canAccessHistory: boolean;
  canAccessBoundaries: boolean;
  canAccessDrawings: boolean;
  canAccessDocuments: boolean;
  canAccessUserPreferences: boolean;
  canAccessMaps: boolean;
  canAccessMyFleet: boolean;
  canAccessPro: boolean;
  canAccessSanctions: boolean;
  canAccessRiMaritimeAreas: boolean;
  canAccessTimeline: boolean;
  canAccessMapTools: boolean;
  canAccessGraphs: boolean;
  canAccessNearbyVessels: boolean;
  canAccessWeather: boolean;
  canAccessShippingTrafficV2: boolean;
  canMultiSelect: boolean;
  canAccessIncidentsDateRangeIndicator: boolean;
  canAccessBathymetry: boolean;
  canAccessDatumRings: boolean;
  canUploadRTZ: boolean;
  canDrawPoints: boolean;
  canAccessPortHeadline: boolean;
  canAccessHistoryWebSocket: boolean;
  canAccessHighTierHistoryWS: boolean;
  canAccessVPS: boolean;
  canAccessFathom: boolean;
  canAccessIranConflict: boolean;
};

export type PrivilegesWithFeatureFlags = FeatureFlags & Privileges;

const NO_PRIVILEGES: Privileges = {
  canAccessIncidents: false,
  canAccessPorts: {
    wpi: false,
    riskIntelligence: false,
  },
  canAccessRiCountries: false,
  canAccessTravelAdvice: false,
  canAccessRoutes: false,
  canAccessHistory: false,
  canAccessBoundaries: false,
  canAccessDrawings: false,
  canAccessDocuments: false,
  canAccessUserPreferences: false,
  canAccessMaps: false,
  canAccessMyFleet: false,
  canAccessPro: false,
  canAccessSanctions: false,
  canAccessRiMaritimeAreas: false,
  canAccessTimeline: false,
  canAccessMapTools: false,
  canAccessGraphs: false,
  canAccessNearbyVessels: false,
  canAccessWeather: false,
  canAccessShippingTrafficV2: false,
  canMultiSelect: false,
  canAccessIncidentsDateRangeIndicator: false,
  canAccessBathymetry: false,
  canAccessDatumRings: false,
  canUploadRTZ: false,
  canDrawPoints: false,
  canAccessPortHeadline: false,
  canAccessHistoryWebSocket: false,
  canAccessHighTierHistoryWS: false,
  canAccessVPS: false,
  canAccessFathom: false,
  canAccessIranConflict: false,
};

const UNAUTHENTICATED_USER_PRIVILEGES: Privileges = {
  ...NO_PRIVILEGES,
  canAccessPorts: {
    ...NO_PRIVILEGES.canAccessPorts,
    wpi: true,
  },
  canAccessSanctions: true,
  canAccessFathom: true,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const authenticatedUserPrivileges = (userToken: Token): Privileges => ({
  ...NO_PRIVILEGES,
  canAccessIncidents: true,
  // Todo - group name defined by env var?
  // userToken.groups?.includes('staff') || false,
  canAccessPorts: {
    wpi: true,
    riskIntelligence: {
      land: true,
      coastal: true,
      cruise: true,
    },
  },
  canAccessRiCountries: true,
  canAccessTravelAdvice: true,
  canAccessRoutes: true,
  canAccessHistory: userToken.roles?.includes(STAFF_USER_ROLE) || false,
  canAccessBoundaries: true,
  canAccessDrawings: true,
  canAccessDocuments: true,
  canAccessUserPreferences: true,
  canAccessMaps: true,
  canAccessMyFleet: true,
  canAccessPro: true,
  canAccessSanctions: true,
  canAccessRiMaritimeAreas: true,
  canAccessTimeline: true,
  canAccessMapTools: true,
  canAccessGraphs: true,
  canAccessNearbyVessels: userToken.roles?.includes(STAFF_USER_ROLE) || false,
  canAccessWeather: true,
  canAccessShippingTrafficV2:
    userToken.roles?.includes(STAFF_USER_ROLE) || false,
  canMultiSelect: true,
  canAccessBathymetry: true,
  canUploadRTZ: true,
  canDrawPoints: true,
  canAccessPortHeadline: true,
  canAccessHistoryWebSocket: true,
  canAccessHighTierHistoryWS: true,
  canAccessVPS: true,
  canAccessFathom: true,
  canAccessIranConflict: true,
});

export const calculatePrivileges = (
  userToken: Token | null,
  featureFlags: FeatureFlags
): PrivilegesWithFeatureFlags => {
  // Todo - handle unauthenticated user privileges

  if (!userToken) {
    return { ...UNAUTHENTICATED_USER_PRIVILEGES, ...featureFlags };
  }

  // uses environment variables to override default privileges.
  // camelcasifies the environment variable name and checks if it's a boolean
  // eg) REACT_APP_CAN_ACCESS_INCIDENTS=false overrides canAccessIncidents: true
  const environmentPrivilegeOverrides = Object.entries(process.env)
    .filter(([key]) => key.startsWith('REACT_APP_'))
    .map(([key, value]) => [camelCasify(key.replace('REACT_APP_', '')), value])
    .reduce((acc, [key, value]) => {
      if (value?.toLowerCase() === 'true' || value?.toLowerCase() === 'false') {
        acc[key! as keyof PrivilegesWithFeatureFlags] = value === 'true';
      }
      return acc;
    }, {} as Record<string, boolean>);

  return {
    ...authenticatedUserPrivileges(userToken),
    ...environmentPrivilegeOverrides,
    ...featureFlags,
  };
};
